import { NodeType } from '@getaccept/editor-lib';
import type { Row, Section, ContractLink, ContractListItem } from '@getaccept/editor-lib';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { RecipientStatus } from '@getaccept/lib-shared/src/recipients/enums/recipient-status';
import type { User } from '@getaccept/lib-shared/src/users/types/user';

export class ContractHelper {
  static hasAccessToContractLink(
    node: ContractLink,
    recipient: Recipient,
    user?: User,
    isEditing = false
  ) {
    return (
      Boolean(user?.id && (isEditing || node?.contractId)) ||
      node?.recipientMaps?.some(map => map.dsrRecipientId === recipient?.id)
    );
  }

  static sectionContainsContractLink(section: Section) {
    return section.rows.some(row =>
      row.cells.some(cell => cell.nodes.some(node => node.type === NodeType.ContractLink))
    );
  }

  static recipientHasAccessToContractLinkInSection(
    section: Section,
    recipient: Recipient,
    user?: User
  ) {
    return section.rows.some(row =>
      row.cells.some(cell =>
        cell.nodes.some(node => this.hasAccessToContractLink(node, recipient, user))
      )
    );
  }

  static findInaccessibleContractLinksInRow(
    row: Row,
    recipient: Recipient,
    user?: User,
    isEditing = false
  ) {
    return row.cells.some(cell =>
      cell.nodes.some(
        node =>
          node.type === NodeType.ContractLink &&
          !this.hasAccessToContractLink(node, recipient, user, isEditing)
      )
    );
  }

  static filterSectionByContractLinkAccess(
    section: Section,
    recipient: Recipient,
    user?: User,
    isEditing = false
  ) {
    return !(
      section.rows.length === 1 &&
      this.findInaccessibleContractLinksInRow(section.rows[0], recipient, user, isEditing)
    );
  }

  static filterRowByContractLinkAccess(row: Row, recipient: Recipient, user?: User) {
    return !this.findInaccessibleContractLinksInRow(row, recipient, user);
  }

  static updateContractRecipientStatus(
    contract: ContractListItem,
    recipientId: string,
    status: RecipientStatus
  ) {
    return {
      ...contract,
      recipients: contract.recipients.map(recipient =>
        recipient.id === recipientId ? { ...recipient, status } : recipient
      ),
    };
  }
}
